/**
 * MUTAGIM.SHOP — Reviews Marquee Component
 * Bandeau défilant des avis clients
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/api/')) return `${API_URL}${imagePath}`;
  return `${API_URL}/api/uploads/${imagePath}`;
};

export function ReviewsMarquee() {
  const { i18n } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const lang = i18n.language?.split('-')[0] || 'fr';

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        // Only fetch 5-star reviews for the marquee
        const res = await fetch(`${API_URL}/api/products/reviews/random?limit=15&min_rating=5`);
        if (res.ok) {
          const data = await res.json();
          setReviews(data.reviews || []);
        }
      } catch (err) {
        console.error('[ReviewsMarquee] Error fetching reviews:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Don't render if no reviews
  if (loading || reviews.length === 0) {
    return null;
  }

  // Duplicate reviews for seamless scrolling
  const duplicatedReviews = [...reviews, ...reviews, ...reviews];

  // Render stars
  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3 h-3 ${
              star <= rating 
                ? 'text-yellow-400 fill-yellow-400' 
                : 'text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  // Get review content in current language
  const getContent = (content) => {
    if (!content) return '';
    if (typeof content === 'string') return content;
    return content[lang] || content.fr || content.en || '';
  };

  // Get product name in current language
  const getProductName = (product) => {
    if (!product?.name) return '';
    if (typeof product.name === 'string') return product.name;
    return product.name[lang] || product.name.fr || product.name.en || '';
  };

  return (
    <section 
      className="w-full overflow-hidden py-8 bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950"
      data-testid="reviews-marquee"
    >
      {/* Section Title */}
      <div className="text-center mb-6 px-4">
        <h3 className="text-lg font-semibold text-white">
          {lang === 'fr' ? 'Ce que nos clients disent' : lang === 'en' ? 'What Our Customers Say' : 'מה הלקוחות שלנו אומרים'}
        </h3>
        <p className="text-sm text-gray-400 mt-1">
          {lang === 'fr' ? 'Avis 5 étoiles vérifiés de notre communauté' : lang === 'en' ? 'Verified 5-star reviews from our community' : 'ביקורות 5 כוכבים מאומתות מהקהילה שלנו'}
        </p>
        <Link 
          to="/reviews"
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 rounded-full text-sm text-indigo-300 hover:text-white transition-all"
          data-testid="view-all-reviews-btn"
        >
          {lang === 'fr' ? 'Voir tous les avis' : lang === 'en' ? 'View all reviews' : 'צפה בכל הביקורות'}
          <Star className="w-4 h-4" />
        </Link>
      </div>

      {/* Scrolling Reviews */}
      <div className="relative">
        <div 
          className="flex items-stretch gap-4 animate-reviews-marquee"
          style={{ width: 'max-content' }}
        >
          {duplicatedReviews.map((review, index) => (
            <div
              key={`${review.id}-${index}`}
              className="flex-shrink-0 w-[320px] bg-zinc-700/60 backdrop-blur-sm border border-zinc-600/50 rounded-xl p-4 hover:border-zinc-500 hover:bg-zinc-700/80 transition-all shadow-lg"
            >
              {/* Header: Stars + Author */}
              <div className="flex items-center justify-between mb-3">
                {renderStars(review.rating)}
                <span className="text-xs text-gray-300">
                  {typeof review.author_name === 'object'
                    ? (review.author_name[lang] || review.author_name.fr || review.author_name.en || 'Client')
                    : (review.author_name || 'Client vérifié')}
                </span>
              </div>
              
              {/* Review Content */}
              <p className="text-sm text-gray-200 line-clamp-3 mb-3 min-h-[60px]">
                {review.title && (
                  <strong className="text-white">
                    {typeof review.title === 'object' 
                      ? (review.title[lang] || review.title.fr || review.title.en || '') 
                      : review.title} — 
                  </strong>
                )}
                {getContent(review.content)}
              </p>
              
              {/* Product Info with Link */}
              {review.product && review.product.slug && (
                <Link
                  to={`/product/${review.product.slug}`}
                  className="flex items-center gap-3 pt-3 border-t border-zinc-600/50 group"
                >
                  {review.product.image && (
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-zinc-600 flex-shrink-0">
                      <img 
                        src={getImageUrl(review.product.image)}
                        alt={getProductName(review.product)}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-300 truncate group-hover:text-white transition-colors">
                      {getProductName(review.product)}
                    </p>
                    <p className="text-xs text-indigo-400 group-hover:text-indigo-300 transition-colors">
                      {lang === 'fr' ? 'Voir le produit →' : lang === 'en' ? 'View product →' : 'צפה במוצר →'}
                    </p>
                  </div>
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes reviews-marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        .animate-reviews-marquee {
          animation: reviews-marquee 60s linear infinite;
        }
        .animate-reviews-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
}

export default ReviewsMarquee;
